/**
 * TransparentComparison Component
 *
 * Side-by-side transparent comparison of REPAIR vs REPLACE.
 * Pure logic - no emotion. Shows all the numbers clearly.
 * Lets the customer make an informed decision with full transparency.
 */

export default function TransparentComparison({
  repairCost,
  currentAge,
  currentSystemProjection,
  bestOption,
  bestOptionSavings,
  totalRebates
}) {
  // Calculate totals for comparison
  const repair5YearTotal = (currentSystemProjection?.totals?.grand || 0) + repairCost;
  const replace5YearCost = bestOption?.starting_price || 0;
  const replace5YearEnergy = bestOptionSavings?.energy?.annualSavings ?
    (bestOptionSavings.energy.newSystemAnnualCost * 5) : 0;
  const replace5YearMaintenance = 750; // 5 years × $150 avg new system maintenance
  const replace5YearTotal = (replace5YearCost - totalRebates) + replace5YearEnergy + replace5YearMaintenance;

  const difference = repair5YearTotal - replace5YearTotal;
  const replaceIsCheaper = difference > 0;

  return (
    <div style={{
      background: 'white',
      borderRadius: '12px',
      padding: '30px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
      marginBottom: '25px'
    }}>
      {/* Header */}
      <div style={{
        borderBottom: '3px solid #64748b',
        paddingBottom: '15px',
        marginBottom: '25px'
      }}>
        <div style={{
          fontSize: '13px',
          fontWeight: '700',
          color: '#64748b',
          textTransform: 'uppercase',
          letterSpacing: '1px',
          marginBottom: '8px'
        }}>
          Transparent Financial Analysis
        </div>
        <h3 style={{
          margin: 0,
          fontSize: '24px',
          color: '#1e293b',
          fontWeight: '700'
        }}>
          Repair vs Replace: 5-Year Comparison
        </h3>
        <p style={{
          margin: '8px 0 0 0',
          fontSize: '15px',
          color: '#64748b',
          lineHeight: '1.5'
        }}>
          Here's a complete side-by-side comparison to help you make an informed decision:
        </p>
      </div>

      {/* Comparison Grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '20px',
        marginBottom: '25px'
      }}>
        {/* REPAIR Column */}
        <div style={{
          padding: '25px',
          background: '#fef3c7',
          borderRadius: '12px',
          border: '3px solid #fbbf24'
        }}>
          <h4 style={{
            margin: '0 0 20px 0',
            fontSize: '20px',
            fontWeight: '700',
            color: '#92400e',
            textAlign: 'center',
            paddingBottom: '12px',
            borderBottom: '2px solid #fbbf24'
          }}>
            🔧 REPAIR Current System
          </h4>

          <div style={{ marginBottom: '20px' }}>
            <div style={{
              fontSize: '13px',
              color: '#92400e',
              marginBottom: '4px',
              fontWeight: '600'
            }}>
              Immediate Cost
            </div>
            <div style={{ fontSize: '24px', fontWeight: '700', color: '#1e293b' }}>
              ${repairCost.toLocaleString()}
            </div>
          </div>

          <div style={{
            padding: '15px',
            background: 'white',
            borderRadius: '8px',
            marginBottom: '15px',
            border: '1px solid #fde68a'
          }}>
            <div style={{ fontSize: '13px', color: '#92400e', marginBottom: '12px', fontWeight: '600' }}>
              5-Year Costs (Age {currentAge} → {currentAge + 5})
            </div>
            <div style={{ fontSize: '14px', marginBottom: '8px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                <span style={{ color: '#64748b' }}>Repair Now:</span>
                <strong>${repairCost.toLocaleString()}</strong>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                <span style={{ color: '#64748b' }}>Energy Costs:</span>
                <strong>${(currentSystemProjection?.totals?.energy || 0).toLocaleString()}</strong>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                <span style={{ color: '#64748b' }}>Maintenance:</span>
                <strong>${(currentSystemProjection?.totals?.maintenance || 0).toLocaleString()}</strong>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                <span style={{ color: '#64748b' }}>Expected Repairs:</span>
                <strong style={{ color: '#dc2626' }}>
                  ${(currentSystemProjection?.totals?.repairs || 0).toLocaleString()}
                </strong>
              </div>
            </div>
          </div>

          <div style={{
            padding: '15px',
            background: '#fed7aa',
            borderRadius: '8px',
            border: '2px solid #fb923c',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '13px', color: '#7c2d12', marginBottom: '6px', fontWeight: '600' }}>
              5-Year Total Cost
            </div>
            <div style={{ fontSize: '32px', fontWeight: '800', color: '#ea580c' }}>
              ${repair5YearTotal.toLocaleString()}
            </div>
          </div>

          <div style={{
            marginTop: '15px',
            padding: '12px',
            background: 'white',
            borderRadius: '6px',
            fontSize: '12px',
            color: '#92400e',
            border: '1px solid #fde68a'
          }}>
            <div style={{ fontWeight: '600', marginBottom: '6px' }}>⚠️ Additional Considerations:</div>
            <ul style={{ margin: '0', paddingLeft: '18px' }}>
              <li>System will be {currentAge + 5} years old by end of period</li>
              <li>No warranty coverage on aging components</li>
              <li>Comfort issues will persist</li>
              <li>High energy waste continues</li>
            </ul>
          </div>
        </div>

        {/* REPLACE Column */}
        <div style={{
          padding: '25px',
          background: '#d1fae5',
          borderRadius: '12px',
          border: '3px solid #10b981'
        }}>
          <h4 style={{
            margin: '0 0 20px 0',
            fontSize: '20px',
            fontWeight: '700',
            color: '#065f46',
            textAlign: 'center',
            paddingBottom: '12px',
            borderBottom: '2px solid #10b981'
          }}>
            ✨ REPLACE with New System
          </h4>

          <div style={{ marginBottom: '20px' }}>
            <div style={{
              fontSize: '13px',
              color: '#065f46',
              marginBottom: '4px',
              fontWeight: '600'
            }}>
              System Cost (Before Rebates)
            </div>
            <div style={{ fontSize: '24px', fontWeight: '700', color: '#1e293b' }}>
              ${(bestOption?.starting_price || 0).toLocaleString()}
            </div>
            {totalRebates > 0 && (
              <div style={{ fontSize: '16px', color: '#059669', marginTop: '4px' }}>
                <strong>-${totalRebates.toLocaleString()}</strong> in rebates
              </div>
            )}
          </div>

          <div style={{
            padding: '15px',
            background: 'white',
            borderRadius: '8px',
            marginBottom: '15px',
            border: '1px solid #a7f3d0'
          }}>
            <div style={{ fontSize: '13px', color: '#065f46', marginBottom: '12px', fontWeight: '600' }}>
              5-Year Costs (New System)
            </div>
            <div style={{ fontSize: '14px', marginBottom: '8px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                <span style={{ color: '#64748b' }}>System Price:</span>
                <strong>${(bestOption?.starting_price || 0).toLocaleString()}</strong>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                <span style={{ color: '#10b981' }}>Rebates:</span>
                <strong style={{ color: '#10b981' }}>-${totalRebates.toLocaleString()}</strong>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                <span style={{ color: '#64748b' }}>Energy Costs:</span>
                <strong>${Math.round(replace5YearEnergy).toLocaleString()}</strong>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                <span style={{ color: '#64748b' }}>Maintenance:</span>
                <strong>${replace5YearMaintenance.toLocaleString()}</strong>
              </div>
            </div>
          </div>

          <div style={{
            padding: '15px',
            background: '#6ee7b7',
            borderRadius: '8px',
            border: '2px solid #10b981',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '13px', color: '#064e3b', marginBottom: '6px', fontWeight: '600' }}>
              5-Year Total Cost
            </div>
            <div style={{ fontSize: '32px', fontWeight: '800', color: '#047857' }}>
              ${Math.round(replace5YearTotal).toLocaleString()}
            </div>
          </div>

          <div style={{
            marginTop: '15px',
            padding: '12px',
            background: 'white',
            borderRadius: '6px',
            fontSize: '12px',
            color: '#065f46',
            border: '1px solid #a7f3d0'
          }}>
            <div style={{ fontWeight: '600', marginBottom: '6px' }}>✅ Additional Benefits:</div>
            <ul style={{ margin: '0', paddingLeft: '18px' }}>
              <li>New equipment warranty (typically 10 years parts)</li>
              <li>All comfort issues resolved</li>
              <li>Maximum energy efficiency</li>
              <li>Peace of mind, no breakdown risk</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Line */}
      <div style={{
        padding: '25px',
        background: replaceIsCheaper ? '#ecfdf5' : '#fffbeb',
        borderRadius: '12px',
        border: replaceIsCheaper ? '3px solid #10b981' : '3px solid #fbbf24'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '15px'
        }}>
          <div>
            <h4 style={{
              margin: '0 0 6px 0',
              fontSize: '20px',
              fontWeight: '700',
              color: replaceIsCheaper ? '#065f46' : '#92400e'
            }}>
              {replaceIsCheaper ? '✅ Replace Saves You Money' : '⚖️ Similar 5-Year Costs'}
            </h4>
            <p style={{
              margin: 0,
              fontSize: '14px',
              color: replaceIsCheaper ? '#065f46' : '#92400e'
            }}>
              5-year cost difference:
            </p>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{
              fontSize: '36px',
              fontWeight: '800',
              color: replaceIsCheaper ? '#059669' : '#d97706'
            }}>
              {replaceIsCheaper ? (
                <span>${Math.abs(difference).toLocaleString()}</span>
              ) : (
                <span>~${Math.abs(difference).toLocaleString()}</span>
              )}
            </div>
            <div style={{
              fontSize: '14px',
              color: replaceIsCheaper ? '#065f46' : '#92400e',
              fontWeight: '600'
            }}>
              {replaceIsCheaper ? 'saved by replacing' : 'cost difference'}
            </div>
          </div>
        </div>

        <div style={{
          paddingTop: '15px',
          borderTop: '2px solid ' + (replaceIsCheaper ? '#86efac' : '#fcd34d'),
          fontSize: '14px',
          color: replaceIsCheaper ? '#065f46' : '#92400e',
          lineHeight: '1.6'
        }}>
          <strong>📊 Transparent Analysis:</strong> {replaceIsCheaper ? (
            <>
              Over the next 5 years, replacing your system will cost you ${Math.abs(difference).toLocaleString()} LESS
              than repairing and keeping your current system—and you'll enjoy superior comfort, efficiency,
              and peace of mind the entire time.
            </>
          ) : (
            <>
              Over the next 5 years, the total costs are similar. However, replacing gives you a brand new system
              with warranty protection, resolved comfort issues, and maximum efficiency—while repairing
              leaves you with an aging system that's only getting older and less reliable.
            </>
          )}
        </div>
      </div>

      {/* Methodology Note */}
      <div style={{
        marginTop: '20px',
        padding: '15px 20px',
        background: '#f1f5f9',
        borderRadius: '8px',
        border: '1px solid #cbd5e1'
      }}>
        <div style={{
          fontSize: '12px',
          color: '#475569',
          lineHeight: '1.5'
        }}>
          <strong>📋 Methodology:</strong> This comparison includes all projected costs over 5 years
          including energy (with 3% annual utility escalation), routine maintenance, and expected repairs
          based on system age and failure probability. Replacement costs include equipment, rebates, and
          5 years of operation. All figures are estimates for comparison purposes. Your comfort consultant
          will provide exact quotes.
        </div>
      </div>
    </div>
  );
}
